import _ from 'lodash'
import { resolveDice, type DiceExpr } from '~/assets/dice/diceEval'
import generators from '~/assets/names/nameData'
import { either, generate, type GenContext, type GenNode } from '~/assets/names/nameGen'

export interface CharEditorData {
  name: string
  title: string
  race: RaceData
  class: string
  subclass: string
  alignment: string
  age: string
}

export interface LockData {
  name?: boolean
  race?: boolean
  class?: boolean
  age?: boolean
  alignment?: boolean
}

interface CharGenContext {
  char: CharEditorData
  lock: LockData
  gen: GenContext
}

export const alignments = ['lg', 'ng', 'cg', 'ln', 'tn', 'cn', 'le', 'ne', 'ce']

interface RaceData extends OptionData<RaceData> {
  race?: string
  subrace?: string
  age?: DiceExpr
  names?: () => GenNode
}

interface OptionData<T> {
  weight?: number
  options?: (T & { weight?: number })[]
}

export const races: (RaceData & OptionData<RaceData>)[] = [
  {
    race: 'human',
    weight: 20,
    age: '4d12+50',
    options: [
      {
        subrace: 'maritime',
        names: () => generators['human']['maritime name'],
      },
      {
        subrace: 'martial',
        names: () => generators['human']['martial name'],
      },
      {
        subrace: 'mystical',
        names: () => generators['human']['mystical name'],
      },
      {
        subrace: 'agrarian',
        names: () => generators['human']['agrarian name'],
      },
      {
        subrace: 'nomadic',
        names: () => generators['human']['virtue name'], // todo: nomadic names
      },
    ],
  },
  {
    race: 'elf',
    weight: 8,
    names: () => generators['elf']['wood elf name'],
    age: '10d12+140',
    options: [
      {
        subrace: 'high',
        names: () => generators['elf']['high elf name'],
      },
      {
        subrace: 'wood',
        weight: 2,
        names: () => generators['elf']['wood elf name'], },
      {
        race: 'half-elf',
        age: '7d12+100',
        weight: 2,
      },
    ],
  },
  {
    race: 'dwarf',
    weight: 5,
    age: '5d12+100',
    names: () => generators['dwarf']['name'],
    options: [{ subrace: 'hill', weight: 2 }, { subrace: 'mountain' }],
  },
  {
    // smallfolk
    options: [
      {
        race: 'gnome',
        weight: 3,
        age: '4d12+90',
        names: () => generators['gnome']['name'],
        options: [{ subrace: 'forest', weight: 2 }, { subrace: 'rock' }],
      },
      {
        race: 'halfling',
        weight: 3,
        age: '3d12+75',
        names: () => generators['halfling']['name'],
        options: [{ weight: 5 }, { subrace: 'middin' }],
      },
    ],
  },
  {
    // planetouched
    weight: 3,
    names: () => generators['human']['virtue name'],
    options: [
      {
        race: 'tiefling',
        options: [{ subrace: 'chtonic' }, { subrace: 'infernal', weight: 2 }, { subrace: 'abyssal' }],
      },
      { race: 'aasimar' },
      { race: 'changeling' },
      { race: 'astralite' },
      {
        race: 'genasi',
        options: [{ subrace: 'fire' }, { subrace: 'earth' }, { subrace: 'water' }, { subrace: 'air' }],
      },
    ],
  },
  {
    // sylvanites
    weight: 3,
    names: () => generators['human']['virtue name'],
    options: [
      { race: 'tabaxi', weight: 3 },
      { race: 'aquilon' },
      { race: 'canior' },
      { race: 'simian' },
      { race: 'smallkin' },
      { race: 'ursinian' },
      { race: 'harengon' },
      { race: 'aarakocra' },
      { race: 'centaur' },
      { race: 'faunkin' },
    ],
  },
  {
    // monstrous
    weight: 3,
    names: () => generators['human']['virtue name'],
    options: [
      { race: 'orc' },
      { race: 'half-orc' },
      { race: 'goliath' },
      {
        race: 'dragonborn',
        names: () => generators['dragon']['name'],
        options: [
          { subrace: 'red' },
          { subrace: 'blue' },
          { subrace: 'black' },
          { subrace: 'green' },
          { subrace: 'white' },
          { subrace: 'gold' },
          { subrace: 'silver' },
          { subrace: 'bronze' },
          { subrace: 'brass' },
          { subrace: 'copper' },
        ],
      },
    ],
  },
  {
    // exotic
    weight: 3,
    names: () => generators['human']['virtue name'],
    options: [{ race: 'warforged' }, { race: 'gravewalker' }],
  },
]

interface ClassData {
  name: string
  weight?: number
  subclasses: string[]
}
export const classes: ClassData[] = [
  {
    name: 'artificer',
    subclasses: ['artillerist', 'armorer', 'alchemist', 'battlesmith', 'gunsmith', 'grenadier'],
  },
  {
    name: 'barbarian',
    subclasses: ['wildheart'],
  },
  {
    name: 'bard',
    subclasses: ['valor'],
  },
  {
    name: 'cleric',
    subclasses: ['life'],
  },
  {
    name: 'druid',
    subclasses: ['moon'],
  },
  {
    name: 'fighter',
    subclasses: ['champion'],
  },
  {
    name: 'monk',
    subclasses: ['mercy'],
  },
  {
    name: 'paladin',
    subclasses: ['vengeance'],
  },
  {
    name: 'ranger',
    subclasses: ['hunter'],
  },
  {
    name: 'rogue',
    subclasses: ['assassin'],
  },
  {
    name: 'sorcerer',
    subclasses: ['wild magic'],
  },
  {
    name: 'spellsword',
    subclasses: ['magehunter'],
  },
  {
    name: 'warlock',
    subclasses: ['archfey'],
  },
  {
    name: 'wizard',
    subclasses: ['illusionist', 'alchemist'],
  },
]

function pick<T>(ts: (T & OptionData<T>)[]): T & OptionData<T> {
  const cumulativeSum = (
    (sum) => (value: number) =>
      (sum += value)
  )(0)
  const weights = ts.map((t) => t.weight || 1)
  const cumulativeWeights = weights.map(cumulativeSum)
  const totalCount = cumulativeSum(0)
  const wIndex = Math.ceil(Math.random() * totalCount)
  const index = cumulativeWeights.findIndex((weight) => weight >= wIndex)
  return ts[index]
}

function any<T>(list: T[]): T {
  const index = Math.floor(Math.random() * list.length)
  return list[index]
}

const randomRace = (ctx: CharGenContext) => {
  // todo: subrace lock
  if (ctx.lock.race) return
  const base = pick(races)
  const subrace = base.options ? pick(base.options) : {}
  const lineage = subrace.options ? pick(subrace.options) : {}
  const race = { ...base, ...subrace, ...lineage }
  ctx.char.race = race
}

const randomName = (ctx: CharGenContext) => {
  if (ctx.lock.name) return
  if (!ctx.char.race.names) return ''
  const name = generate(ctx.char.race.names(), ctx.gen)
  ctx.char.name = name.replaceAll(/\(.*?\)/g, '').replaceAll(/\s+/g, ' ') // remove elvish translations
}

const randomAge = (ctx: CharGenContext) => {
  if (ctx.lock.age) return
  if (!ctx.char.race.age) return ''
  const max = resolveDice(ctx.char.race.age, {})
  const min = 15
  const a = _.random(min, max)
  const b = _.random(min, max)
  ctx.char.age = `${Math.min(a, b)}` // first-order approximation of a normal distribution
}

const randomClass = (ctx: CharGenContext) => {
  if (ctx.lock.class) return
  const cls = pick(classes)
  const subclass = any(cls.subclasses)
  ctx.char.class = cls.name
  ctx.char.subclass = subclass
}

const randomAlignment = (ctx: CharGenContext) => {
  if (ctx.lock.alignment) return
  ctx.char.alignment = generate(either(...alignments), ctx.gen, false)
}

export function randomChar(char: CharEditorData, lock: LockData): CharEditorData {
  const ctx: CharGenContext = {
    char,
    lock,
    gen: { type: '', cache: new Set<string>() },
  }
  randomRace(ctx)
  randomClass(ctx)
  randomName(ctx)
  randomAge(ctx)
  randomAlignment(ctx)
  return ctx.char
}
